import React from "react";
import { Inbox, Mail, MailX, MailWarning, ChevronRight } from "lucide-react";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { formLabel } from "../../lib/forms";
import { cn } from "../../lib/utils";

export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const STATUS_STYLE = {
  new: "border-primary bg-primary text-primary-foreground",
  read: "border-border text-foreground",
  replied: "border-emerald-500/50 text-emerald-400",
  archived: "border-border text-muted-foreground",
};

export const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={cn("rounded-none text-[10px] uppercase tracking-[0.18em]", STATUS_STYLE[status] || STATUS_STYLE.read)} data-testid={`status-badge-${status}`}>
    {status}
  </Badge>
);

const FORM_STYLE = { guest_spot: "text-sky-300", lead: "text-primary", contact: "text-violet-300" };

export const FormTypeLabel = ({ type }) => <span className={cn("text-[10px] font-semibold uppercase tracking-[0.2em]", FORM_STYLE[type] || "text-foreground")}>{formLabel(type)}</span>;

/** Delivery icons: one per email kind on this submission. */
export const EmailStatus = ({ emails = [] }) => {
  if (!emails.length) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="flex items-center gap-1.5" data-testid="email-status">
      {emails.map((e, i) => {
        const Icon = e.status === "sent" ? Mail : e.status === "failed" ? MailX : MailWarning;
        const tone = e.status === "sent" ? "text-emerald-400" : e.status === "failed" ? "text-destructive" : "text-muted-foreground";
        return <Icon key={`${e.kind}-${i}`} className={cn("h-4 w-4", tone)} title={`${e.kind}: ${e.status}${e.error ? ` — ${e.error}` : ""}`} />;
      })}
    </span>
  );
};

const Th = ({ children, className = "" }) => <TableHead className={`text-[10px] uppercase tracking-[0.2em] ${className}`}>{children}</TableHead>;

const SubmissionsTable = ({ items, onOpen, emptyTitle = "No submissions yet" }) => {
  if (items === null) {
    return (
      <div className="space-y-2" data-testid="submissions-loading">
        {["a", "b", "c"].map((k) => (
          <Skeleton key={k} className="h-14" />
        ))}
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="flex flex-col items-center border border-border bg-card px-6 py-20 text-center" data-testid="submissions-empty">
        <Inbox className="h-8 w-8 text-primary" />
        <p className="display mt-4 text-4xl uppercase">{emptyTitle}</p>
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">Guest-spot requests, promo leads and contact messages will show up here the moment someone submits a form.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-border bg-card" data-testid="submissions-table">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <Th>Received</Th>
            <Th>Form</Th>
            <Th>From</Th>
            <Th className="hidden md:table-cell">Message</Th>
            <Th className="hidden lg:table-cell">Source</Th>
            <Th>Email</Th>
            <Th>Status</Th>
            <Th className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id} onClick={() => onOpen(s.id)} className={cn("cursor-pointer", s.status === "new" && "bg-primary/[0.04]")} data-testid={`submission-row-${s.id}`}>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(s.created_at)}</TableCell>
              <TableCell>
                <FormTypeLabel type={s.form_type} />
              </TableCell>
              <TableCell>
                <p className={cn("text-sm", s.status === "new" && "font-semibold")}>{s.name || "—"}</p>
                <p className="text-xs text-muted-foreground">{s.email}</p>
              </TableCell>
              <TableCell className="hidden max-w-[320px] md:table-cell">
                <p className="truncate text-sm text-muted-foreground">{s.fields?.message || "—"}</p>
              </TableCell>
              <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">
                {s.page_title ? <span className="text-foreground">{s.page_title}</span> : s.path || "—"}
                {s.utm_source && <span className="block">via {s.utm_source}</span>}
              </TableCell>
              <TableCell>
                <EmailStatus emails={s.emails} />
              </TableCell>
              <TableCell>
                <StatusBadge status={s.status} />
              </TableCell>
              <TableCell>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SubmissionsTable;
